<template>
  <TemplateBase :page="page">
    <!-- Slot with back button with flush false so it is positioned to the header -->
    <template #backButton>
      <MoleculeButtonBreadcrumbBack :breadcrumbs="breadcrumbs" background-color="bg-app-secondary-7-violet--purple-heart" />
    </template>

    <!-- Slot with mobile lyre link component, color is optional in white, default is black, only visible until xl screens, positioning is inside the component -->
    <template #mobileLyre>
      <MoleculeButtonMobileLyre :flush="false" :white-lyre="true" />
    </template>

    <div class="grid grid-cols-12 v-gradient-bg v-gradient-bg--violet">
      <section v-if="page.image" class="col-span-full row-start-1 bg-transparent z-0 v-margin-page-image-to-page-content">
        <MoleculeMaskVisual :image="page.image" />
      </section>

      <OrganismPageSectionContent
        class="row-start-2 col-span-full mb-10 md:mb-12 xl:mb-16"
        :at-page-top="!page.image"
        background-color="bg-transparent sm:bg-white"
        :title="page.title"
        :introduction="page.introduction"
        :theme="'violet'"
      >
      </OrganismPageSectionContent>

      <section class="col-span-full row-start-3 container mx-auto lg:grid lg:grid-cols-12 gap-x-8 v-page-section-container-padding mb-12 xl:mb-20">
        <header class="col-span-full xl:col-start-2 xl:col-end-12 flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 md:mb-8">
          <ul class="flex flex-wrap gap-2 md:gap-4 mb-4 sm:mb-0">
            <li v-for="overview in page.overviews" :key="overview.url" class="flex-1 sm:flex-initial">
              <MoleculeButton
                v-if="overview.season"
                :url="overview.url"
                :enable-icon-left="false"
                :background-color="{
                  'bg-white hover:bg-black': overview.url === page.url,
                  'bg-app-secondary-7-violet--fog hover:bg-black': overview.url !== page.url,
                }"
                text-color="text-black hover:text-white"
                size="small"
                class="w-full md:hidden"
              >
                <AtomIconAppHat class="shrink-0 w-4 md:w-5 h-4 md:h-5 mr-2" />
                {{ overview.season.title }}
              </MoleculeButton>
              <MoleculeButton
                v-if="overview.season"
                :url="overview.url"
                :enable-icon-left="false"
                :background-color="{
                  'bg-white hover:bg-black': overview.url === page.url,
                  'bg-app-secondary-7-violet--fog hover:bg-black': overview.url !== page.url,
                }"
                text-color="text-black hover:text-white"
                size="medium"
                class="hidden md:flex"
              >
                <AtomIconAppHat class="shrink-0 w-4 md:w-5 h-4 md:h-5 mr-2" />
                {{ overview.season.title }}
              </MoleculeButton>
            </li>
          </ul>
          <AtomLink
            v-if="page.archive && page.archive.url !== page.url"
            :url="page.archive.url"
            class="self-end sm:self-auto text-xs md:text-base text-white hover:text-app-secondary-7-violet--fog underline u-text-underline-offset--2 font-semibold flex items-center transform transition duration-200"
          >
            <AtomIconAppArchive class="shrink-0 w-3 md:w-6 h-3 md:h-6 mr-1 md:mr-2" />
            {{ $t('education.archive.button') }}
          </AtomLink>
        </header>

        <ul class="col-span-full xl:col-start-2 xl:col-end-12">
          <li v-for="production in productions" :key="production.url" class="mb-6">
            <MoleculeCardEducation
              :labels="production.schoolTypes.map((schoolType: CmsData) => schoolType.title)"
              :image="production.teaserImage"
              :location="production.room ? production.room.title : undefined"
              :season="production.season ? production.season.title : undefined"
              :title="production.title"
              :price="production.price"
              :duration="production.duration"
              :url="production.url"
            />
          </li>
        </ul>
      </section>
    </div>
  </TemplateBase>
</template>

<script setup lang="ts">
const props = defineProps({
  breadcrumbs: { type: Array as PropType<any[]>, required: true },
  page: { type: Object as PropType<Record<string, any>>, required: true },
})

const pageStore = usePageStore()

const { data: productions } = await useAsyncData(`education-production-overview;${props.page.url}`, async () => {
  const pages = await Promise.all(props.page.productions.map(({ uri }: CmsData) => pageStore.fetchUri({ uri })))
  return pages.filter((page) => !!page)
})
</script>
